/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { BasicCard } from '@coursera-web/cds-labs';

import { useParams } from 'js/lib/useRouter';

import { Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { SkillMasteryLevelFragment } from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { getSkillsMasteryLevelLabels } from 'bundles/levelsets/levelsets-v2/constants';
import type { Skill } from 'bundles/levelsets/levelsets-v2/types';
import { getLatestMasteryProgress, getMasteryLevelNumber } from 'bundles/levelsets/levelsets-v2/utils/formatConversion';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { generateSkillsPageUrl } from 'bundles/skills-common/private/utils/skillLinkUtils';

import _t from 'i18n!nls/levelsets';

type MasteryLevel = SkillMasteryLevelFragment['masteryLevel'];

type Props = {
  skill: Skill;
  masteryLevel?: MasteryLevel;
  masteryPercent?: number;
  index?: number;
};

const styles = {
  fullHeightContainer: css`
    height: 100%;

    > div {
      height: 100%;
    }

    & :hover {
      transform: none;
      box-shadow: none;
    }
  `,

  cardContainer: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-spacing-200);
    padding: var(--cds-spacing-200);
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  link: css`
    color: var(--cds-color-neutral-primary);

    &:hover,
    &:focus {
      color: var(--cds-color-neutral-primary);
    }

    &:visited:not(.cds-link-focusVisible) {
      color: var(--cds-color-neutral-primary);
    }
  `,

  linkSection: css`
    display: flex;
    gap: var(--cds-spacing-200);
    margin-top: auto;
  `,

  currentMasteryContainer: css`
    display: flex;
    gap: var(--cds-spacing-50);
    align-items: center;
    margin-bottom: var(--cds-spacing-50);
  `,
  skillName: css`
    margin-bottom: 2px;
  `,
};

const getEvaluateButtonText = (attainedMasteryLevel: MasteryLevel | null, masteryPercent?: number) => {
  return attainedMasteryLevel || masteryPercent ? _t('Try again') : _t('Evaluate');
};

export const HomeLevelSetCard = ({ skill, masteryLevel, masteryPercent, index }: Props) => {
  const track = useTracker();
  const { programSlug } = useParams();
  const { nextLevel } = useHeaderLevel();

  const attemptUrl = `/levelsets/assessment/${skill.skillId}?programSlug=${programSlug}`;
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const recommendationsUrl = `${generateSkillsPageUrl(skill.skillId, programSlug)}?mastery=true`;

  const { attainedMasteryLevel } = getLatestMasteryProgress(masteryLevel, masteryPercent);

  const masteryLevelLabels = getSkillsMasteryLevelLabels();
  const MasteryLevelIcon = attainedMasteryLevel
    ? masteryLevelLabels[attainedMasteryLevel].icon
    : () => <React.Fragment />;

  const trackedSkill = {
    id: skill.skillId,
    name: skill.skillName,
  };
  const trackingData = {
    skill: trackedSkill,
    enterprise: {
      programSlug,
    },
    carousel: { id: 'levelsets' },
    levelsetCard: {
      levelsetCardVariant: 'primary',
      index,
      hasLearnerAttemptedBefore: !!masteryLevel,
      learnerMasteryLevel: getMasteryLevelNumber(attainedMasteryLevel),
      skill: trackedSkill,
    },
  };

  const viewRecommendationsLink = (
    <Link
      key={recommendationsUrl}
      css={styles.link}
      href={recommendationsUrl}
      tabIndex={0}
      aria-label={_t('View recommendations')}
      onClick={() => {
        track('click_view_skill_recommendations', {
          ...trackingData,
          button: { name: 'levelsets2_card_view_recommendations' },
        });
      }}
    >
      {_t('View recommendations')}
    </Link>
  );

  const evaluationLink = (
    <Link
      key={attemptUrl}
      css={styles.link}
      href={attemptUrl}
      tabIndex={0}
      aria-label={getEvaluateButtonText(attainedMasteryLevel, masteryPercent)}
      onClick={() => {
        track('click_assess_skill_with_levelset', {
          ...trackingData,
          button: {
            name: attainedMasteryLevel ? 'levelsets2_card_reattempt' : 'levelsets2_card_attempt',
            linkType: 'internal',
            linkURL: attemptUrl,
          },
        });
      }}
    >
      {getEvaluateButtonText(attainedMasteryLevel, masteryPercent)}
    </Link>
  );

  const cardLinks = [viewRecommendationsLink, evaluationLink];

  return (
    <div css={styles.fullHeightContainer}>
      <BasicCard css={styles.cardContainer} aria-label={skill.skillName} id="levelset-card">
        <Typography2 variant="subtitleMedium" component={nextLevel} css={styles.skillName}>
          {skill.skillName}
        </Typography2>

        {attainedMasteryLevel && (
          <div css={styles.currentMasteryContainer}>
            <MasteryLevelIcon />
            <Typography2 component="p" variant="bodySecondary">
              {`${masteryLevelLabels[attainedMasteryLevel].name}: ${masteryLevelLabels[attainedMasteryLevel].description}`}
            </Typography2>
          </div>
        )}

        <div css={styles.linkSection}>
          {cardLinks.map((link) => (
            <React.Fragment key={link.key}>{link}</React.Fragment>
          ))}
        </div>
      </BasicCard>
    </div>
  );
};
